import React, { FunctionComponent, Suspense, useEffect, useMemo, useState } from 'react';
import AutomotiveBreadcrumbs from '../automotivePage/AutomotiveBreadcrumbs';
import { AnimatePresence, motion } from 'framer-motion';
import { HomeConsumer } from './ConsumerImageComponent';
import { LowVoltDcImage, PowerConversionImage } from '../computingPage/CommercialSVGs';
import Typography from '@mui/material/Typography';
import { ApplicationsConsumerContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import { useLocation, useNavigate } from 'react-router-dom';

type ComponentName = string;

interface HomeConsumerDisplayProps {
    cmsContent: ApplicationsConsumerContent;
}

interface ComponentInfo {
    label: string;
    component?: React.ComponentType<any>;
    imageComponent?: React.ComponentType<any>;
}

const getImageComponent = (key: string): React.ComponentType<any> => {
    const powerTypes = ['homeAppliances', 'powerTools'];
    return powerTypes.includes(key) ? PowerConversionImage : LowVoltDcImage;
};

const generateLabelMap = (
    cmsContent: ApplicationsConsumerContent
): Map<string | number, string> => {
    return new Map([
        ['homeappliances', cmsContent.HomeConsumer.imageMapLinkTexts.homeAppliances.content],
        ['iot', cmsContent.HomeConsumer.imageMapLinkTexts.iot.content],
        ['hometheater', cmsContent.HomeConsumer.imageMapLinkTexts.homeTheater.content],
        [
            'portableelectronics',
            cmsContent.HomeConsumer.imageMapLinkTexts.portableElectronics.content,
        ],
        ['powertools', cmsContent.HomeConsumer.imageMapLinkTexts.powerTools.content],
        [
            'peripheralsandprinters',
            cmsContent.HomeConsumer.imageMapLinkTexts.peripheralsAndPrinters.content,
        ],
        ['gaming', cmsContent.HomeConsumer.imageMapLinkTexts.gaming.content],
    ]);
};

const HomeConsumerDisplay: FunctionComponent<HomeConsumerDisplayProps> = ({ cmsContent }) => {
    const [componentStack, setComponentStack] = useState<ComponentName[]>(['consumer']);
    const navigate = useNavigate();
    const labelMap = generateLabelMap(cmsContent);
    const location = useLocation();

    const components = useMemo(() => {
        const base: Record<string, ComponentInfo> = {
            consumer: {
                label: cmsContent.HomeConsumer.title.content,
                component: HomeConsumer,
            },
        };
        const imageMapTexts = cmsContent?.HomeConsumer?.imageMapLinkTexts;
        if (imageMapTexts && typeof imageMapTexts === 'object') {
            Object.entries(imageMapTexts).forEach(([key, value]) => {
                const id = key.toLowerCase();
                base[id] = {
                    label: value.content,
                    imageComponent: getImageComponent(key),
                };
            });
        }
        return base;
    }, [cmsContent]);

    const allowed = useMemo(() => new Set(Object.keys(components)), [components]);

    useEffect(() => {
        const y = location.state?.scrollPosition;
        if (typeof y === 'number') {
            requestAnimationFrame(() => window.scrollTo(0, y));
        }
    }, [location.pathname]);

    useEffect(() => {
        const segs = location.pathname.toLowerCase().split('/').filter(Boolean);
        const idx = segs.indexOf('consumer');
        const relevant = idx !== -1 ? segs.slice(idx + 1) : [];
        const own = relevant.filter((s) => allowed.has(s));
        setComponentStack(own.length ? ['consumer', ...own] : ['consumer']);
    }, [location.pathname, allowed]);

    const handleComponentSwitch = (componentName: ComponentName) => {
        const id = String(componentName).toLowerCase();
        if (!components[id]) {
            console.warn(`Ignored click: ${id} is not in component map`);
            return;
        }
        const scrollPosition = window.scrollY;
        const newStack = [...componentStack, id];
        setComponentStack(newStack);
        const path = `/applications/consumer/${newStack.slice(1).join('/')}`;
        navigate(path, { state: { scrollPosition }, preventScrollReset: true });
    };

    const handleBack = () => {
        const scrollPosition = window.scrollY;
        if (componentStack.length > 1) {
            const newStack = componentStack.slice(0, -1);
            setComponentStack(newStack);
            const path = `/applications/consumer/${newStack.slice(1).join('/')}`;
            navigate(path, { state: { scrollPosition }, preventScrollReset: true });
        } else {
            setComponentStack(['consumer']);
            navigate(`/applications/consumer`, {
                state: { scrollPosition },
                preventScrollReset: true,
            });
        }
    };

    const fadeVariants = {
        initial: { opacity: 0 },
        animate: { opacity: 1 },
        exit: { opacity: 0 },
    };

    const fadeTransition = {
        duration: 0.8,
        ease: 'easeInOut',
    };

    const handleBreadcrumbClick = (index: number) => {
        setComponentStack((prev) => prev.slice(0, index + 1));
    };

    const currentComponentName = componentStack[componentStack.length - 1] || 'consumer';

    const currentComponentInfo = components.hasOwnProperty(currentComponentName)
        ? components[currentComponentName]
        : undefined;

    const breadcrumbItems = componentStack.map((name) => ({
        label: components[name]?.label || name,
    }));

    return (
        <>
            <AutomotiveBreadcrumbs
                items={breadcrumbItems}
                onBreadcrumbClick={handleBreadcrumbClick}
            />
            <Typography sx={{ marginBottom: '2rem', fontFamily: 'Poppins', fontSize: '16px' }} />
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentComponentName}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    variants={fadeVariants}
                    transition={fadeTransition}
                >
                    <Suspense fallback={<div>Loading...</div>}>
                        {currentComponentInfo?.imageComponent ? (
                            <currentComponentInfo.imageComponent onBack={handleBack} />
                        ) : currentComponentInfo?.component ? (
                            <currentComponentInfo.component
                                cmsContent={cmsContent.HomeConsumer}
                                onBack={handleBack}
                                onAnnotationClick={handleComponentSwitch}
                                labelMap={labelMap}
                            />
                        ) : null}
                    </Suspense>
                </motion.div>
            </AnimatePresence>
        </>
    );
};

export default HomeConsumerDisplay;
